import { useState, type FC } from 'react'

import { MessagePopup, Select } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useQuery } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { LocalDateTime } from '@/components/LocalDateTime'

import adminQueries from '../queries'
import adminResources from '../resources'
import type { FeatureOriginal } from '../resources/features'

type Companies = {
  [key: string]: string
}

export const ActivationHistory: FC<{
  feature: FeatureOriginal
  onClose: () => void
}> = ({ feature, onClose }) => {
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null)
  const translate = useTranslation()

  const { data, isLoading } = useQuery(
    adminQueries.adminFeatureActivities(feature.id),
    () => adminResources.getFeatureActivities(feature.id),
    {
      enabled: Boolean(feature.id),
    }
  )

  // Do not show the activity log if there is no history yet
  if (!data || data?.length === 0 || isLoading) return null

  const distinctCompanies = data.reduce((acc: Companies, event) => {
    const customer = event.customerFeature.customer

    if (!acc[String(customer.id)]) {
      acc[String(customer.id)] = customer.name
    }

    return acc
  }, {})

  const dataFiltered = data.filter(item => {
    if (!selectedCustomerId) return true

    return item.customerFeature.customer.id === selectedCustomerId
  })

  return (
    <MessagePopup
      title={<Translation id="Activity history" />}
      isOpen={Boolean(data?.length)}
      onClose={onClose}
      variant="wide"
    >
      <div className="mb-2">
        <Select
          options={Object.keys(distinctCompanies).map(key => ({
            label: distinctCompanies[key],
            value: key,
          }))}
          id="company"
          onChange={args => setSelectedCustomerId(Number(args?.value))}
          value={String(selectedCustomerId)}
          isClearable
          placeholder={translate('Select company')}
        />
      </div>
      <div className="max-h-96 overflow-y-auto">
        <table>
          <thead>
            <tr>
              <th>
                <Translation id="Date" />
              </th>

              <th>
                <Translation id="Customer" />
              </th>

              <th>
                <Translation id="Manager" />
              </th>

              <th>
                <Translation id="Modification type" />
              </th>
            </tr>
          </thead>

          <tbody>
            {dataFiltered?.map(event => (
              <tr key={event.id}>
                <td>
                  <LocalDateTime date={event.timestamp} />
                </td>
                <td>{event?.customerFeature?.customer?.name}</td>
                <td>{`${event.user.firstName} ${event.user.lastName}`}</td>
                <td>{event.action}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex justify-end">
        <Button variant="tertiary" onClick={onClose}>
          <Translation id="Close" />
        </Button>
      </div>
    </MessagePopup>
  )
}
